import { useMemo, useState } from "react";
import { Download, FileDown } from "lucide-react";

import { createIdempotencyScope } from "@/core/api/idempotency";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useRequestStatement } from "@/features/wallet/api/use-request-statement";
import { useDownloadStatement } from "@/features/wallet/api/use-download-statement";
import type {
  StatementRequestDto,
  StatementRequestResponse,
} from "@/features/wallet/api/dto";

const toInputDate = (date: Date) => date.toISOString().slice(0, 10);

/** Extrait le `statementId` du `downloadUrl` (`…/Statements/requests/{id}/download`). */
const statementIdFromResponse = (
  response: StatementRequestResponse
): string | null =>
  response.downloadUrl?.match(/Statements\/requests\/([^/]+)\/download/)?.[1] ??
  response.statementId ??
  null;

/**
 * Carte « Demander un relevé » — `POST /Statements/request` (guide v2.3 §6.3,
 * réponse v3.1) puis téléchargement du PDF via
 * `GET /Statements/requests/{statementId}/download` (guide v3.1, bouton
 * affiché dès que la réponse porte `downloadUrl` — relevé recomposé à la
 * demande, GET de consultation hors scope des previews).
 *
 * wallet-service compose un PDF puis le fait expédier par notification-service
 * à l'adresse enregistrée du titulaire. Pour un back-office / multi-portefeuilles,
 * `walletId` est obligatoire — on le fournit toujours ici.
 */
export function WalletStatementRequestCard({ walletId }: { walletId: string }) {
  const { t } = useTypedTranslation();
  const tReq = (key: string) => t(key as never, {} as never) as string;
  const request = useRequestStatement();
  const download = useDownloadStatement();

  const defaults = useMemo(() => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - 30);
    return { from: toInputDate(from), to: toInputDate(today) };
  }, []);

  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [preview, setPreview] = useState<StatementRequestDto | null>(null);
  /** Dernière demande aboutie — `downloadUrl` est immédiat (relevé recomposé à la demande). */
  const [lastStatement, setLastStatement] =
    useState<StatementRequestResponse | null>(null);
  const statementId = lastStatement
    ? statementIdFromResponse(lastStatement)
    : null;
  // Scope d'idempotence : même clé au retry, nouvelle clé à chaque confirmation.
  const scope = useMemo(() => createIdempotencyScope(), []);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {tReq("wallet.statements.card.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {tReq("wallet.statements.card.description")}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="statement-from">
                {tReq("wallet.statements.card.from")}
              </Label>
              <Input
                id="statement-from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="statement-to">
                {tReq("wallet.statements.card.to")}
              </Label>
              <Input
                id="statement-to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button
                className="w-full"
                disabled={!from || !to || request.isPending}
                onClick={() => setPreview({ walletId, from, to })}
              >
                {request.isPending ? (
                  <ButtonSpinner />
                ) : (
                  <>
                    <FileDown className="mr-2 w-4 h-4" />
                    {tReq("wallet.statements.card.action")}
                  </>
                )}
              </Button>
            </div>
          </div>
          {statementId && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                disabled={download.isPending}
                onClick={() =>
                  download.mutate(
                    { statementId },
                    {
                      onSuccess: () =>
                        notifSilence(
                          tReq("wallet.statements.download.success")
                        ),
                      onError: (error) => notifSilenceError(error.message),
                    }
                  )
                }
              >
                {download.isPending ? (
                  <ButtonSpinner />
                ) : (
                  <>
                    <Download className="mr-2 w-4 h-4" />
                    {tReq("wallet.statements.download.action")}
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
        data={preview ?? {}}
        title={tReq("wallet.statements.preview.title")}
        description={tReq("wallet.statements.preview.description")}
        confirmLabel={tReq("wallet.statements.card.action")}
        pending={request.isPending}
        onConfirm={() => {
          if (!preview) return;
          request.mutate(
            { body: preview, idempotencyKey: scope.key },
            {
              onSuccess: (response) => {
                notifSilence(tReq("wallet.statements.feedback.sent"));
                setLastStatement(response);
                scope.renew();
                setPreview(null);
              },
              onError: (error) => notifSilenceError(error.message),
            }
          );
        }}
      />
    </>
  );
}
